/**
 * Inline SVG icon set — 24×24 viewBox, stroke = currentColor, so each icon
 * inherits the text color of its button and scales with `size`. Lucide-style
 * 1.75px strokes for a crisp look at 16px. No icon-library dependency.
 */

import type { ReactNode } from "react";

interface IconProps {
  size?: number;
  className?: string;
}

function Svg({ size = 16, className, children }: IconProps & { children: ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** Brand mark — three linked nodes (a synapse). */
export const IconBrand = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="6" cy="6" r="2.2" />
    <circle cx="18" cy="9" r="2.2" />
    <circle cx="9" cy="18" r="2.2" />
    <path d="M7.9 7 16 8.7M16.3 10.9 10.6 16.1" />
  </Svg>
);

/** Skills — sparkles. */
export const IconSkills = (p: IconProps) => (
  <Svg {...p}>
    <path d="M11.5 3.2a.5.5 0 0 1 .95 0l1.36 4.06a2 2 0 0 0 1.27 1.27l4.06 1.36a.5.5 0 0 1 0 .95l-4.06 1.36a2 2 0 0 0-1.27 1.27l-1.36 4.06a.5.5 0 0 1-.95 0l-1.36-4.06a2 2 0 0 0-1.27-1.27L4.54 12.2a.5.5 0 0 1 0-.95l4.06-1.36a2 2 0 0 0 1.27-1.27z" />
    <path d="M19 14v3M20.5 15.5h-3M5 4v2M6 5H4" />
  </Svg>
);

/** Pipelines — workflow blocks. */
export const IconPipelines = (p: IconProps) => (
  <Svg {...p}>
    <rect width="8" height="8" x="3" y="3" rx="2" />
    <path d="M7 11v4a2 2 0 0 0 2 2h4" />
    <rect width="8" height="8" x="13" y="13" rx="2" />
  </Svg>
);

/** Sources — git branch. */
export const IconSources = (p: IconProps) => (
  <Svg {...p}>
    <line x1="6" x2="6" y1="3" y2="15" />
    <circle cx="18" cy="6" r="3" />
    <circle cx="6" cy="18" r="3" />
    <path d="M18 9a9 9 0 0 1-9 9" />
  </Svg>
);

/** Changes — pull request. */
export const IconChanges = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="18" cy="18" r="3" />
    <circle cx="6" cy="6" r="3" />
    <path d="M13 6h3a2 2 0 0 1 2 2v7" />
    <line x1="6" x2="6" y1="9" y2="21" />
  </Svg>
);

/** Members — users. */
export const IconMembers = (p: IconProps) => (
  <Svg {...p}>
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </Svg>
);

/** API clients — a key. */
export const IconKey = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="7.5" cy="15.5" r="5.5" />
    <path d="M21 2l-9.6 9.6M15.5 7.5l3 3M18.5 4.5l3 3" />
  </Svg>
);

/** Ingest — upload. */
export const IconIngest = (p: IconProps) => (
  <Svg {...p}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M17 8l-5-5-5 5" />
    <line x1="12" x2="12" y1="3" y2="15" />
  </Svg>
);

/** Source — GitHub mark. */
export const IconGithub = (p: IconProps) => (
  <Svg {...p}>
    <path d="M15 22v-4a4.8 4.8 0 0 0-1-3.5c3 0 6-2 6-5.5.08-1.25-.27-2.48-1-3.5.28-1.15.28-2.35 0-3.5 0 0-1 0-3 1.5-2.64-.5-5.36-.5-8 0C6 2 5 2 5 2c-.3 1.15-.3 2.35 0 3.5A5.4 5.4 0 0 0 4 9c0 3.5 3 5.5 6 5.5-.39.49-.68 1.05-.85 1.65-.17.6-.22 1.23-.15 1.85v4" />
    <path d="M9 18c-4.51 2-5-2-7-2" />
  </Svg>
);

/** Account — user avatar. */
export const IconUser = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="10" r="3.2" />
    <path d="M6.2 19a6 6 0 0 1 11.6 0" />
  </Svg>
);

/** Chevron — dropdown affordance. */
export const IconChevronDown = (p: IconProps) => (
  <Svg {...p}>
    <path d="m6 9 6 6 6-6" />
  </Svg>
);

/** Chevron — rightward disclosure. */
export const IconChevronRight = (p: IconProps) => (
  <Svg {...p}>
    <path d="m9 18 6-6-6-6" />
  </Svg>
);

/** Search — magnifier. */
export const IconSearch = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.2-3.2" />
  </Svg>
);

/** New — plus. */
export const IconPlus = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);

/** Send — arrow up. */
export const IconSend = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 19V5M5 12l7-7 7 7" />
  </Svg>
);

/** Attach — paperclip. */
export const IconPaperclip = (p: IconProps) => (
  <Svg {...p}>
    <path d="M21.4 11.05 12.2 20.2a5.5 5.5 0 0 1-7.8-7.8l9.2-9.15a3.7 3.7 0 0 1 5.2 5.2l-9.2 9.2a1.8 1.8 0 0 1-2.6-2.6l8.5-8.5" />
  </Svg>
);

/** Copy — duplicate. */
export const IconCopy = (p: IconProps) => (
  <Svg {...p}>
    <rect width="12" height="12" x="9" y="9" rx="2.2" />
    <path d="M5 15a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2" />
  </Svg>
);

/** Done — check. */
export const IconCheck = (p: IconProps) => (
  <Svg {...p}>
    <path d="m4.5 12.5 5 5 10-11" />
  </Svg>
);

/** Dismiss — cross. */
export const IconX = (p: IconProps) => (
  <Svg {...p}>
    <path d="M18 6 6 18M6 6l12 12" />
  </Svg>
);

/** Failed — cross in a circle. */
export const IconXCircle = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="m15 9-6 6M9 9l6 6" />
  </Svg>
);

/** In flight — a partial ring, spun by CSS. */
export const IconSpinner = (p: IconProps) => (
  <Svg {...p} className={`spinner${p.className ? ` ${p.className}` : ""}`}>
    <path d="M21 12a9 9 0 1 1-6.2-8.6" />
  </Svg>
);

/** Waiting — empty circle. */
export const IconCircle = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="12" r="8.5" />
  </Svg>
);

/** Reasoning — sparkle. */
export const IconSparkle = (p: IconProps) => (
  <Svg {...p}>
    <path d="M12 3.2 13.9 9a2 2 0 0 0 1.3 1.3l5.8 1.9-5.8 1.9A2 2 0 0 0 13.9 15L12 20.8 10.1 15a2 2 0 0 0-1.3-1.3L3 11.8l5.8-1.9A2 2 0 0 0 10.1 9z" />
  </Svg>
);

/** Synapse page — document. */
export const IconPage = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" />
    <path d="M14 3v5h5M9 13h6M9 17h4" />
  </Svg>
);

/** Chats — message bubble. */
export const IconChat = (p: IconProps) => (
  <Svg {...p}>
    <path d="M20 15a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z" />
  </Svg>
);

/** Sidebar — collapse / expand the rail. */
export const IconPanelLeft = (p: IconProps) => (
  <Svg {...p}>
    <rect width="18" height="18" x="3" y="3" rx="2.2" />
    <path d="M9.5 3v18" />
  </Svg>
);

/** Menu — drawer handle. */
export const IconMenu = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 6h16M4 12h16M4 18h16" />
  </Svg>
);

/** Open elsewhere — external link. */
export const IconExternal = (p: IconProps) => (
  <Svg {...p}>
    <path d="M14 4h6v6M20 4l-8.5 8.5" />
    <path d="M18 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h5" />
  </Svg>
);

/** Sign out — exit. */
export const IconLogout = (p: IconProps) => (
  <Svg {...p}>
    <path d="M15 17l5-5-5-5M20 12H9" />
    <path d="M12 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h6" />
  </Svg>
);

/** Artifact — download. */
export const IconDownload = (p: IconProps) => (
  <Svg {...p}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <path d="M7 10l5 5 5-5" />
    <line x1="12" x2="12" y1="3" y2="15" />
  </Svg>
);
